import { Boxes, Fingerprint } from "lucide-react";
import { type JSX, type MouseEvent, useState } from "react";
import type { PqcInventorySummary, SbomInventorySummary } from "../../api/types.js";
import { Badge } from "../../components/Badge.js";
import { formatInteger } from "../../lib/format.js";
import { displayRuleId } from "../../app/rule-id.js";
import type {
  InventoryCbomRow,
  InventorySbomRow,
  InventorySelection,
} from "./inventory-selection.js";
import "./inventory-queue.css";

export const CBOM_PAGE_SIZE = 50;
export const SBOM_PAGE_SIZE = 50;

export function cryptoDisplayName(row: InventoryCbomRow): string {
  const use = row.useContext.replaceAll("_", " ");
  if (row.algorithm?.toLocaleLowerCase() === "ec") {
    return `Elliptic-curve (EC) ${use}`;
  }
  if (
    row.algorithm &&
    /^\d{3,5}$/u.test(row.algorithm) &&
    /key establishment|key generation/u.test(use)
  ) {
    return `RSA-${row.algorithm} ${use}`;
  }
  if (row.algorithm) return `${row.algorithm} ${use}`;
  return `Dynamic algorithm ${use}`;
}

function cbomIdentityId(row: InventoryCbomRow): string {
  if (row.findingRuleId) return displayRuleId(row.findingRuleId);
  if (row.algorithm && /^\d{3,5}$/u.test(row.algorithm)) return `RSA-${row.algorithm}`;
  if (row.algorithm) return row.algorithm;
  return "CBOM";
}

function classificationTone(algorithmClass: InventoryCbomRow["algorithmClass"]) {
  if (algorithmClass === "quantum_vulnerable_public_key") return "critical" as const;
  if (algorithmClass === "hybrid") return "warning" as const;
  if (algorithmClass === "post_quantum_standardized") return "positive" as const;
  return "neutral" as const;
}

function pageWindow<T>(rows: readonly T[], page: number, pageSize: number) {
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const visiblePage = Math.min(Math.max(1, page), pageCount);
  const start = (visiblePage - 1) * pageSize;
  return {
    pageCount,
    visiblePage,
    start,
    rows: rows.slice(start, start + pageSize),
  };
}

function QueuePager({
  label,
  noun,
  total,
  pageSize,
  visiblePage,
  pageCount,
  start,
  onPage,
}: {
  label: string;
  noun: string;
  total: number;
  pageSize: number;
  visiblePage: number;
  pageCount: number;
  start: number;
  onPage: (page: number) => void;
}) {
  if (total <= pageSize) return null;
  return (
    <nav className="pagination" aria-label={label}>
      <button
        className="button button--small"
        type="button"
        disabled={visiblePage === 1}
        onClick={() => onPage(Math.max(1, visiblePage - 1))}
      >
        Previous
      </button>
      <span>
        Page {visiblePage} of {pageCount} · showing {start + 1}–{Math.min(start + pageSize, total)}{" "}
        of {formatInteger(total)} {noun}
      </span>
      <button
        className="button button--small"
        type="button"
        disabled={visiblePage === pageCount}
        onClick={() => onPage(Math.min(pageCount, visiblePage + 1))}
      >
        Next
      </button>
    </nav>
  );
}

export function InventoryQueue(props: {
  sbom: SbomInventorySummary;
  cbom: PqcInventorySummary;
  selection: InventorySelection | null;
  onSelect: (selection: InventorySelection) => void;
}): JSX.Element {
  const { sbom, cbom, selection, onSelect } = props;
  const [cbomPage, setCbomPage] = useState(1);
  const [sbomPage, setSbomPage] = useState(1);
  const cbomWindow = pageWindow(cbom.rows, cbomPage, CBOM_PAGE_SIZE);
  const sbomWindow = pageWindow(sbom.rows, sbomPage, SBOM_PAGE_SIZE);

  return (
    <>
      <section className="page-section inventory-queue-section" aria-labelledby="cbom-heading">
        <div className="inspector-section__heading">
          <Fingerprint aria-hidden="true" size={18} />
          <h2 id="cbom-heading">Cryptographic bill of materials</h2>
        </div>
        <p className="muted-copy inventory-queue-section__intro">
          Code-level cryptographic uses with evidence and migration context.
        </p>
        {cbom.available ? (
          <>
            <QueuePager
              label="CBOM crypto pages"
              noun="crypto uses"
              total={cbom.rows.length}
              pageSize={CBOM_PAGE_SIZE}
              visiblePage={cbomWindow.visiblePage}
              pageCount={cbomWindow.pageCount}
              start={cbomWindow.start}
              onPage={setCbomPage}
            />
            <div className="table-scroll">
              <table className="data-table inventory-table inventory-queue-table">
                <thead>
                  <tr>
                    <th>Identity</th>
                    <th>Classification</th>
                    <th>Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {cbomWindow.rows.map((row) => {
                    const selected = selection?.kind === "cbom" && selection.row.id === row.id;
                    return (
                      <tr
                        className={
                          selected
                            ? "data-row inventory-queue-row inventory-queue-row--selected"
                            : "data-row inventory-queue-row"
                        }
                        key={row.id}
                        aria-current={selected ? "true" : undefined}
                        onClick={() => onSelect({ kind: "cbom", row })}
                      >
                        <td>
                          <button
                            className="inventory-queue-title"
                            type="button"
                            onClick={() => onSelect({ kind: "cbom", row })}
                          >
                            <code className="inventory-queue-id">{cbomIdentityId(row)}</code>
                            <span>
                              <strong>{cryptoDisplayName(row)}</strong>
                              <small className="inventory-queue-title__story">
                                {row.useContext.replaceAll("_", " ")}
                              </small>
                            </span>
                          </button>
                        </td>
                        <td>
                          <span className="inventory-queue-pills">
                            <Badge
                              label={row.algorithmClass.replaceAll("_", " ")}
                              tone={classificationTone(row.algorithmClass)}
                            />
                            <Badge
                              label={row.findingRuleId ? "Finding-linked" : "Inventory observation"}
                              tone={row.findingRuleId ? "info" : "muted"}
                            />
                          </span>
                        </td>
                        <td>
                          <a
                            className="inventory-queue-evidence"
                            href={row.sourceHref}
                            onClick={(event: MouseEvent<HTMLAnchorElement>) =>
                              event.stopPropagation()
                            }
                          >
                            {row.location.path}:{row.location.lineStart}
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {cbom.complete ? null : <p className="muted-copy">{cbom.limitation}</p>}
          </>
        ) : (
          <p className="unavailable-value">Unavailable — {cbom.limitation}</p>
        )}
      </section>

      <section className="page-section inventory-queue-section" aria-labelledby="sbom-heading">
        <div className="inspector-section__heading">
          <Boxes aria-hidden="true" size={18} />
          <h2 id="sbom-heading">Software bill of materials</h2>
        </div>
        {sbom.available ? (
          <>
            <p className="muted-copy inventory-queue-section__intro">
              Manifest-declared components. Download the complete deterministic CycloneDX 1.5
              inventory.
            </p>
            <QueuePager
              label="SBOM component pages"
              noun="components"
              total={sbom.rows.length}
              pageSize={SBOM_PAGE_SIZE}
              visiblePage={sbomWindow.visiblePage}
              pageCount={sbomWindow.pageCount}
              start={sbomWindow.start}
              onPage={setSbomPage}
            />
            <div className="table-scroll">
              <table className="data-table inventory-table inventory-queue-table">
                <thead>
                  <tr>
                    <th>Component identity</th>
                    <th>Dependency evidence</th>
                    <th>Assurance state</th>
                  </tr>
                </thead>
                <tbody>
                  {sbomWindow.rows.map((component: InventorySbomRow) => {
                    const selected =
                      selection?.kind === "sbom" && selection.row.id === component.id;
                    const identity = component.resolved
                      ? `${component.name}@${component.version}`
                      : component.name;
                    return (
                      <tr
                        className={
                          selected
                            ? "data-row inventory-queue-row inventory-queue-row--selected"
                            : "data-row inventory-queue-row"
                        }
                        key={component.id}
                        aria-current={selected ? "true" : undefined}
                        onClick={() => onSelect({ kind: "sbom", row: component })}
                      >
                        <td>
                          <button
                            className="inventory-queue-title"
                            type="button"
                            onClick={() => onSelect({ kind: "sbom", row: component })}
                          >
                            <code className="inventory-queue-id">{component.ecosystem}</code>
                            <span>
                              <strong>{identity}</strong>
                              <small className="inventory-queue-title__story">
                                {component.purl}
                              </small>
                            </span>
                          </button>
                        </td>
                        <td>
                          <span className="inventory-queue-provenance">
                            <code title={component.manifestPath}>{component.manifestPath}</code>
                            <small>
                              {component.dependencyPath.length > 1
                                ? `Transitive · ${component.dependencyPath.join(" → ")}`
                                : "Direct manifest declaration"}
                            </small>
                          </span>
                        </td>
                        <td>
                          <span className="inventory-queue-pills">
                            <Badge label={component.license ?? "Not declared"} tone="neutral" />
                            <Badge
                              label={component.resolved ? "Pinned" : "Unresolved"}
                              tone={component.resolved ? "info" : "warning"}
                            />
                            {component.local ? <Badge label="Local" tone="muted" /> : null}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {sbom.rows.length > SBOM_PAGE_SIZE ? (
              <p className="muted-copy">
                Use the deterministic CycloneDX download for the complete machine-readable
                inventory.
              </p>
            ) : null}
            {sbom.complete ? null : <p className="muted-copy">{sbom.limitation}</p>}
          </>
        ) : (
          <p className="unavailable-value">Unavailable — {sbom.limitation}</p>
        )}
      </section>
    </>
  );
}
